import type {
  City,
  CalculateResponse,
  PickupPoint,
  CartData,
  AddressSuggestion,
  DeliveryProvider,
  DeliveryOrderPayload,
  DeliveryOrderResponse,
} from '../types';
import { LS_PENDING_ORDERS_KEY } from '../types';

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  async searchCities(query: string, countryCode?: string): Promise<City[]> {
    if (query.length < 2) return [];
    const params = new URLSearchParams({ query, limit: '10' });
    if (countryCode) params.set('country_code', countryCode);
    const response = await fetch(`${this.baseUrl}/cities/search?${params}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to search cities');
    return data.cities;
  }

  async calculate(cdekCityId: number, countryCode: string, cartData: CartData): Promise<CalculateResponse> {
    const response = await fetch(`${this.baseUrl}/delivery/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cdek_city_id: cdekCityId,
        country_code: countryCode,
        total_price: cartData.totalPrice,
        total_weight: cartData.totalWeight,
        dimensions: cartData.dimensions,
        items: cartData.items,
      }),
    });
    const data = await response.json();
    return data;
  }

  async getPickupPoints(cdekCityId: number): Promise<PickupPoint[]> {
    const response = await fetch(`${this.baseUrl}/delivery/pickup-points?cdek_city_id=${cdekCityId}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to get pickup points');
    return data.points;
  }

  async getPickupPointsByProvider(provider: DeliveryProvider, cdekCityId?: number, cityName?: string): Promise<PickupPoint[]> {
    const params = new URLSearchParams();
    if (cdekCityId) params.append('cdek_city_id', String(cdekCityId));
    if (cityName) params.append('city_name', cityName);
    const response = await fetch(`${this.baseUrl}/delivery/pickup-points/${provider}?${params}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to get pickup points');
    return data.points;
  }

  async suggestAddress(query: string, city?: string): Promise<AddressSuggestion[]> {
    if (query.length < 3) return [];
    const params = new URLSearchParams({ query });
    if (city) params.append('city', city);
    try {
      const response = await fetch(`${this.baseUrl}/delivery/address/suggest?${params}`);
      const data = await response.json();
      if (!data.success) { console.warn('[GW Delivery] Address suggestions not available'); return []; }
      return data.suggestions;
    } catch (error) {
      console.warn('[GW Delivery] Address suggestions error:', error);
      return [];
    }
  }

  async getGreenwaxPickup(): Promise<PickupPoint | null> {
    try {
      const response = await fetch(`${this.baseUrl}/delivery/greenwax-pickup`);
      const data = await response.json();
      if (!data.success) return null;
      return data.point;
    } catch (error) {
      console.warn('[GW Delivery] Greenwax pickup error:', error);
      return null;
    }
  }

  /**
   * Submit delivery order to backend
   * POST /api/v1/delivery/orders
   * Retry logic: 1 retry on failure
   * Fallback: save to localStorage if backend is unreachable
   */
  async submitDeliveryOrder(payload: DeliveryOrderPayload): Promise<DeliveryOrderResponse> {
    const maxRetries = 1;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${this.baseUrl}/delivery/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        // Success - remove from pending if it was saved there
        this.removePendingOrder(payload.timestamp);
        console.log('[GW Delivery] Order submitted successfully:', data.orderId);
        return { orderId: data.orderId, success: true };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        console.warn(`[GW Delivery] Order submit attempt ${attempt + 1}/${maxRetries + 1} failed:`, lastError.message);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // All retries failed - save to localStorage as pending
    console.warn('[GW Delivery] Order submit failed, saving to localStorage as pending');
    this.savePendingOrder(payload);
    return { orderId: '', success: false, error: lastError?.message || 'Failed to submit order' };
  }

  /**
   * Flush any pending orders saved in localStorage
   * Call on page load to retry previously failed submissions
   */
  async flushPendingOrders(): Promise<void> {
    const pending = this.getPendingOrders();
    if (pending.length === 0) return;
    console.log(`[GW Delivery] Found ${pending.length} pending order(s), retrying...`);

    for (const order of pending) {
      try {
        const response = await fetch(`${this.baseUrl}/delivery/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(order),
        });
        if (response.ok) {
          const data = await response.json();
          this.removePendingOrder(order.timestamp);
          console.log('[GW Delivery] Pending order flushed successfully:', data.orderId);
        }
      } catch (error) {
        console.warn('[GW Delivery] Failed to flush pending order:', error);
        break;
      }
    }
  }

  private savePendingOrder(payload: DeliveryOrderPayload): void {
    try {
      const pending = this.getPendingOrders();
      const exists = pending.some(o => o.timestamp === payload.timestamp);
      if (!exists) pending.push(payload);
      const trimmed = pending.slice(-10);
      localStorage.setItem(LS_PENDING_ORDERS_KEY, JSON.stringify(trimmed));
    } catch (e) {
      console.warn('[GW Delivery] Failed to save pending order to localStorage:', e);
    }
  }

  private removePendingOrder(timestamp: string): void {
    try {
      const pending = this.getPendingOrders();
      const filtered = pending.filter(o => o.timestamp !== timestamp);
      if (filtered.length > 0) {
        localStorage.setItem(LS_PENDING_ORDERS_KEY, JSON.stringify(filtered));
      } else {
        localStorage.removeItem(LS_PENDING_ORDERS_KEY);
      }
    } catch (e) { /* ignore */ }
  }

  private getPendingOrders(): DeliveryOrderPayload[] {
    try {
      const raw = localStorage.getItem(LS_PENDING_ORDERS_KEY);
      if (!raw) return [];
      return JSON.parse(raw) as DeliveryOrderPayload[];
    } catch { return []; }
  }
}

export default ApiClient;
